import { DateTime } from "luxon";
import { ShowingStatus, ShowingType } from "api/scheduling/types";

export const showingTypeLabels: Record<ShowingType, string> = {
  [ShowingType.Guided]: "Guided",
  [ShowingType.SelfGuided]: "Self-guided"
};

export const colors = {
  content100: "#bec0ca",
  content200: "#A6A8B1",
  content500: "#53545C",

  alpha40: "rgba(28,29,34,0.040)",
  alpha80: "rgba(28,29,34,0.080)",
  alpha100: "rgba(28,29,34,0.1)",

  danger500: "#F4423F"
};

interface ShowingStatusConfig {
  label: string;
  dotClass: string;
  textClass: string;
  bgClass: string;
  eventBg: string;
}

export const showingStatusConfig: Record<ShowingStatus, ShowingStatusConfig> = {
  [ShowingStatus.Confirmed]: {
    label: "Scheduled",
    dotClass: "bg-primary-500",
    textClass: "text-primary-700",
    bgClass: "bg-primary-20",
    eventBg: "var(--primary-20)"
  },
  [ShowingStatus.LeadConfirmed]: {
    label: "Confirmed",
    dotClass: "bg-teal-500",
    textClass: "text-teal-700",
    bgClass: "bg-teal-50",
    eventBg: "#f0fdfa"
  },
  [ShowingStatus.Ongoing]: {
    label: "Ongoing",
    dotClass: "bg-amber-500",
    textClass: "text-amber-700",
    bgClass: "bg-amber-50",
    eventBg: "#fffbeb"
  },
  [ShowingStatus.InProgress]: {
    label: "In progress",
    dotClass: "bg-warning-500",
    textClass: "text-warning-700",
    bgClass: "bg-warning-50",
    eventBg: "#fef6e6"
  },
  [ShowingStatus.Completed]: {
    label: "Completed",
    dotClass: "bg-success-500",
    textClass: "text-success-700",
    bgClass: "bg-success-50",
    eventBg: "#effbeb"
  },
  [ShowingStatus.NoShow]: {
    label: "No-show",
    dotClass: "bg-rose-500",
    textClass: "text-rose-700",
    bgClass: "bg-rose-50",
    eventBg: "#fff1f2"
  },
  [ShowingStatus.Cancelled]: {
    label: "Cancelled",
    dotClass: "bg-gray-400",
    textClass: "text-gray-600",
    bgClass: "bg-gray-100",
    eventBg: "#f3f4f6"
  },
  [ShowingStatus.Pending]: {
    label: "Pending confirmation",
    dotClass: "bg-warning-500",
    textClass: "text-warning-700",
    bgClass: "bg-warning-50",
    eventBg: "#fef6e6"
  },
  [ShowingStatus.Reserved]: {
    label: "Reserved",
    dotClass: "bg-primary-500",
    textClass: "text-primary-500",
    bgClass: "bg-primary-50",
    eventBg: "var(--primary-50)"
  },
  [ShowingStatus.Expired]: {
    label: "Confirmation expired",
    dotClass: "bg-gray-400",
    textClass: "text-gray-600",
    bgClass: "bg-alpha-40",
    eventBg: "#f3f4f6"
  }
};

const RESCHEDULE_REQUESTED_LABEL = "Reschedule requested";

export const showingStatusLabel = (
  status: ShowingStatus,
  reschedulePending?: boolean
): string =>
  status === ShowingStatus.Pending && reschedulePending
    ? RESCHEDULE_REQUESTED_LABEL
    : showingStatusConfig[status].label;

export const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const hour = Math.floor(i / 4);
  const minute = (i % 4) * 15;
  const value = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  return {
    value,
    label: `${hour === 0 || hour === 12 ? 12 : hour % 12}:${String(minute).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}`
  };
});

const formatTimeLabel = (time: string): string => {
  const [h, m] = time.split(":").map(Number);
  const display = h === 0 || h === 12 ? 12 : h % 12;
  return `${display}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};

export const buildToTimeOptions = (
  fromTime: string,
  durationMinutes: number
): { value: string; label: string }[] => {
  if (durationMinutes <= 0) return [];
  const [fh, fm] = fromTime.split(":").map(Number);
  const startMin = fh * 60 + fm;
  const opts: { value: string; label: string }[] = [];
  for (let t = startMin + durationMinutes; t < 24 * 60; t += durationMinutes) {
    const h = Math.floor(t / 60);
    const m = t % 60;
    const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    opts.push({ value, label: formatTimeLabel(value) });
  }
  return opts;
};

export const addMinutesToTime = (time: string, minutes: number): string => {
  const [h, m] = time.split(":").map(Number);
  const total = h * 60 + m + minutes;
  const newH = Math.floor(total / 60) % 24;
  const newM = total % 60;
  return `${String(newH).padStart(2, "0")}:${String(newM).padStart(2, "0")}`;
};

// Latest start that keeps a full slot before midnight (mirrors
// getDefaultStartTime's end-of-day clamp), floored to the 15-min options grid
// so the clamped value is always a valid From option.
export const clampStartTime = (
  time: string,
  durationMinutes: number
): string => {
  const step = durationMinutes > 0 ? durationMinutes : 15;
  const [h, m] = time.split(":").map(Number);
  const maxStart = Math.floor((24 * 60 - step - 15) / 15) * 15;
  if (h * 60 + m <= maxStart) return time;
  return `${String(Math.floor(maxStart / 60)).padStart(2, "0")}:${String(maxStart % 60).padStart(2, "0")}`;
};

export const getDefaultStartTime = (
  timezone: string,
  durationMinutes: number
): string => {
  const step = durationMinutes > 0 ? durationMinutes : 15;
  const now = DateTime.now().setZone(timezone);
  const minutesNow = now.hour * 60 + now.minute;

  let start = Math.ceil(minutesNow / step) * step;
  start = Math.ceil(start / 15) * 15;

  const maxStart = 24 * 60 - step - 15;
  if (start > maxStart) start = maxStart;
  if (start < 0) start = 0;

  const h = Math.floor(start / 60);
  const m = start % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};
